import React, {ReactElement} from 'react';
import {useIntl} from 'react-intl';
import {Field, FieldProps} from 'formik';
import {FormikField} from '@fo/designSystem/Fields/types';
import Email from './Email';
import getRequiredMessage from '../validationMessages/getRequiredMessage';
import getEmailMessage from '../validationMessages/getEmailMessage';

interface Props<FormValues> extends Omit<FormikField, 'name'> {
  name: keyof FormValues;
  maxLength?: number;
  labelClassName?: string;
  labelTestId?: string;
}

function EmailWithFormikField<FormValues>({
  name,
  validate = () => {
    /* noop */
  },
  placeholder,
  label,
  required,
  warningMessage,
  errorMessages,
  maxLength,
  onBlur = () => {
    /* noop */
  },
  isDisabled,
  useCustom,
  className,
  labelClassName,
  testId,
  labelTestId,
}: Props<FormValues>): ReactElement {
  const {formatMessage} = useIntl();
  const validateFieldProp = (value: string) => {
    const requiredMessage = errorMessages?.required || '';
    const requiredErrorMessage = getRequiredMessage(
      formatMessage,
      !!useCustom,
      name,
      value,
      !!required,
      requiredMessage
    );
    if (requiredErrorMessage) {
      return requiredErrorMessage;
    }

    const emailMessage = errorMessages?.email || '';
    // @todo: remove legacy validation
    const legacyMessage = formatMessage({id: 'phoenix.errors.email.validFormat'});
    const emailErrorMessage = getEmailMessage(formatMessage, value, useCustom ? emailMessage : legacyMessage);
    if (emailErrorMessage) {
      return emailErrorMessage;
    }

    // custom validator
    if (value.length > 0) {
      return validate(value);
    }

    return undefined;
  };

  return (
    <Field name={name} validate={validateFieldProp}>
      {({field, meta}: FieldProps) => (
        <Email
          placeholder={placeholder}
          label={label}
          errorMessage={meta.error}
          name={field.name}
          value={field.value}
          onChange={field.onChange}
          onBlur={(e) => {
            field.onBlur(e);
            onBlur(e);
          }}
          warningMessage={warningMessage}
          maxLength={maxLength}
          isDisabled={isDisabled}
          className={className}
          labelClassName={labelClassName}
          testId={testId || `ui.form.field.${field.name}`}
          labelTestId={labelTestId}
        />
      )}
    </Field>
  );
}

export default EmailWithFormikField;
